import { z } from 'zod'

/**
 * Zod validation schema for user invitation form
 * Used with react-hook-form for client-side validation
 */
export const inviteFormSchema = z.object({
  // Normalise BEFORE validating: zod applies these in chain order, so running
  // .email() first rejected pasted addresses that carry surrounding whitespace
  // ("  Test@EXAMPLE.COM  ") instead of trimming them into a valid value.
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Email address is required')
    .email('Please enter a valid email address')
    .max(255, 'Email address is too long'),

  role: z
    .enum(['member', 'admin', 'guest'])
    .default('member'),

  customMessage: z
    .string()
    .max(1000, 'Custom message must be 1000 characters or less')
    .trim()
    .optional(),

  expirationDays: z
    .number()
    .int('Expiration days must be a whole number')
    .min(1, 'Invitation must be valid for at least 1 day')
    .max(30, 'Invitation cannot be valid for more than 30 days')
    .default(7),
})

/**
 * Type inference from Zod schema (using input type for react-hook-form)
 */
export type InviteFormData = z.input<typeof inviteFormSchema>

/**
 * Zod validation schema for accepting an invitation
 */
export const acceptInviteSchema = z.object({
  token: z
    .string()
    .min(1, 'Invitation token is required')
    .trim(),
})

/**
 * Type inference for invite acceptance
 */
export type AcceptInviteData = z.infer<typeof acceptInviteSchema>

/**
 * Client-side validation helper for email format
 * Can be used for real-time validation feedback
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Client-side helper to check if email domain is valid
 * Prevents common typos in email domains
 */
export function validateEmailDomain(email: string): {
  isValid: boolean
  suggestion?: string
} {
  const commonTypos: Record<string, string> = {
    'gmial.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'yahooo.com': 'yahoo.com',
    'hotmial.com': 'hotmail.com',
    'outlok.com': 'outlook.com',
  }

  const domain = email.split('@')[1]?.toLowerCase()

  if (!domain) {
    return { isValid: false }
  }

  // Check for common typos
  if (commonTypos[domain]) {
    return {
      isValid: false,
      suggestion: email.replace(domain, commonTypos[domain]),
    }
  }

  // Basic validity check (has dot and reasonable length)
  const isValid = domain.includes('.') && domain.length >= 4

  return { isValid }
}
